import * as Sentry from '@sentry/react';
import { makeAutoObservable, runInAction, toJS } from 'mobx';
import { makePersistable } from 'mobx-persist-store';

import {
  DeliveryRequests,
  type DeliveryZone,
  type DeliveryZoneCoverage,
} from '~/api/DeliveryAddress';
import {
  DeliveryMechanic,
  DeliveryMethods,
  type DeliverySlotsResponse,
  type ETACalculation,
  type ETACost,
  type ETADeliveryMechanicInfo,
  type ETADeliveryMethod,
  type ETADeliveryMethodInfo,
  type ETADeliveryMethodMechanics,
  type ETADeliveryProviderInfo,
  type ETAPaymentMethod,
  ETARequests,
  type ETAWarehouseMechanicsAvailability,
  type GetETAV3Response,
  MechanicType,
} from '~/api/ETA';
import type {
  MechanicInstance,
  MethodMechanics,
  WarehouseDeliveryMethods,
} from '~/api/ETA/v3/ETA';
import { ETADeliveryMethodType } from '~/api/ETADeliveryMethodType';
import { deliveryAddressStore } from '~/components/ModalDeliveryAddress/DeliveryAddressStore';
import { checkoutStore, mainStore } from '~/stores';
import { storage as localStorage } from '~/stores/LocalStorage';
import { getDeliveryProvider } from '~/utils/deliveryProvider';

import type { BaseStore } from '../interfaces/BaseStore';
import type { IOrderStore } from '../OrderStore/interfaces';

import {
  LEGACY_TO_NEW_DELIVERY_METHOD_NAME,
  NEW_TO_LEGACY_DELIVERY_MECHANIC_NAME,
  NEW_TO_LEGACY_DELIVERY_METHOD_NAME,
} from './constants';
import { DeliveryServiceVersion } from './interfaces';
import { getPrioritizedDeliveryMethod } from './utils';

class DeliveryStore implements BaseStore {
  private readonly _version: DeliveryServiceVersion =
    DeliveryServiceVersion.ONE;
  private readonly _orderStore: IOrderStore;
  private readonly _requestPromiseHolder = new Map<
    string,
    Promise<ETACalculation>
  >();

  deliveryProvider: Nullable<ETADeliveryProviderInfo> = null;
  deliveryMethod: Nullable<ETADeliveryMethodInfo> = null;
  deliveryMechanics: Nullable<ETADeliveryMechanicInfo> = null;
  deliveryProviderByMethod: Record<string, string> = {};

  static DEFAULT_COST: ETACost = {
    shipping: 0,
    shippingPounds: '0',
    threshold: 0,
    thresholdPounds: '0',
    serviceFee: 0,
    serviceFeeShippingPounds: '0',
    serviceFeeThreshold: 0,
    serviceFeeThresholdPounds: '0',
  };
  static async preloadDeliveryVersion(
    companyId: string,
  ): Promise<DeliveryServiceVersion> {
    const response = await DeliveryRequests.getDeliveryConfigByName({
      companyId,
      configName: 'delivery_version',
    });

    const versionMapper: Record<string, DeliveryServiceVersion> = {
      '1': DeliveryServiceVersion.ONE,
      '2': DeliveryServiceVersion.TWO,
    };

    return versionMapper[response] || DeliveryServiceVersion.ONE;
  }

  constructor(orderStore: IOrderStore) {
    this._orderStore = orderStore;

    makeAutoObservable(this, {
      _requestPromiseHolder: false,
    } as any);
    makePersistable(this, {
      name: 'DeliveryStore',
      properties: ['deliveryProviderByMethod'],
      storage: localStorage,
    });

    if (import.meta.env.SSR) {
      return;
    }

    const { deliveryVersion } = window.__INITIAL_STATE__;

    this._version = deliveryVersion;
  }

  get version() {
    return this._version || DeliveryServiceVersion.ONE;
  }

  reset() {
    runInAction(() => {
      this.deliveryProvider = null;
      this.deliveryMethod = null;
      this.deliveryMechanics = null;
      this.deliveryProviderByMethod = {};
    });
  }

  async requestActualVersionETA(
    pos: string,
    deliveryMethod?: ETADeliveryMethodType,
    opts?: {
      receivePrioritizedDeliveryMethod?: boolean;
      signal?: AbortSignal;
    },
  ) {
    if (this._version === DeliveryServiceVersion.ONE) {
      const { data } = await ETARequests.getETA({
        pos,
        seller: 'JIFFY',
        deliveryMethod: deliveryMethod || checkoutStore.deliveryMethod,
        signal: opts?.signal,
      });

      return this._orderStore.etaCalculationResponse(data);
    }

    const key = `${pos}-${deliveryMethod}`;

    if (!this._requestPromiseHolder.has(key)) {
      this._requestPromiseHolder.set(
        key,
        this._executeETARequest(pos, deliveryMethod, opts),
      );
    }

    return this._requestPromiseHolder.get(key) as Promise<ETACalculation>;
  }

  getOrderDeliveryPayload(
    originDeliveryMethod: ETADeliveryMethodType,
    mechanicOrigin?: MechanicType,
  ): {
    delivery_method: string;
    delivery_provider?: string;
    delivery_mechanic?: string;
  } {
    if (this._version === DeliveryServiceVersion.ONE) {
      return { delivery_method: originDeliveryMethod };
    }

    if (originDeliveryMethod === ETADeliveryMethodType.ClickAndCollect) {
      return {
        delivery_provider:
          this.deliveryProviderByMethod[
            ETADeliveryMethodType.ClickAndCollect
          ] ||
          this.deliveryProvider?.code ||
          getDeliveryProvider(),
        delivery_method: this.deliveryMethod?.code || originDeliveryMethod,
        delivery_mechanic:
          this.deliveryMechanics?.PICKUP?.code || DeliveryMechanic.PICKUP,
      };
    }

    const mechanic =
      mechanicOrigin === MechanicType.SLOT
        ? DeliveryMechanic.SCHEDULED
        : DeliveryMechanic.ON_DEMAND;

    const deliveryMethod =
      LEGACY_TO_NEW_DELIVERY_METHOD_NAME[checkoutStore.deliveryMethod] ||
      this.deliveryMethod?.code ||
      DeliveryMethods.COURIER_DELIVERY;

    return {
      delivery_provider:
        this.deliveryProviderByMethod[deliveryMethod] ||
        this.deliveryProvider?.code ||
        getDeliveryProvider(),
      delivery_method: deliveryMethod,
      delivery_mechanic: mechanic,
    };
  }

  async requestActualVersionZones(): Promise<DeliveryZoneCoverage[]> {
    if (this.version === DeliveryServiceVersion.ONE) {
      try {
        const {
          data: { coverages = [] },
        } = await DeliveryRequests.fetchDeliveryZones();
        return coverages;
      } catch (e) {
        console.error(e);
      }
      return [];
    }

    try {
      const {
        data: { coverages = [] },
      } = await DeliveryRequests.fetchDeliveryZonesV2();

      return coverages.map((coverage) => ({
        warehouse: coverage.warehouse,
        zones: coverage.v2zones.map((v2Zone): DeliveryZone | undefined => {
          // TODO zh пока берем первого провайдера из ответа (пока у зоны один провайдер, будет по другому переделать)
          const provider = Object.values(v2Zone.deliveryProviders).at(0);

          const firstDeliveryMethod = Object.values(
            provider?.deliveryMethods ?? {},
          ).at(0);

          if (!firstDeliveryMethod) {
            return;
          }

          const legacyMethodName =
            NEW_TO_LEGACY_DELIVERY_METHOD_NAME[firstDeliveryMethod.code] ||
            firstDeliveryMethod.code;

          const mechanics = Object.entries(
            firstDeliveryMethod.deliveryMechanics || {},
          ).reduce((memo, [mechanicKey, deliveryMechanic]) => {
            const legacyMechanicKey =
              NEW_TO_LEGACY_DELIVERY_MECHANIC_NAME[mechanicKey] || mechanicKey;

            return {
              ...memo,
              [legacyMechanicKey]: {
                schedule: deliveryMechanic.schedule?.value,
              },
            };
          }, {});

          return {
            ...v2Zone,
            deliveryMethod: {
              deliveryMechanics: {
                [legacyMethodName]: mechanics,
              },
              sellers: ['JIFFY', 'jiffy'],
              shippingType: legacyMethodName,
            },
          };
        }),
      }));
    } catch (e: unknown) {
      Sentry.withScope((scope) => {
        scope.setExtras({
          deliveryVersion: 2,
          selectedZone: deliveryAddressStore.selectedZone
            ? toJS(deliveryAddressStore.selectedZone.zone)
            : undefined,
          jsError: e,
        });
        scope.captureMessage(`[ERROR] failed to fetch V2 zones`, 'error');
      });
      return [];
    }
  }

  private async _executeETARequest(
    pos: string,
    deliveryMethod?: ETADeliveryMethodType,
    opts?: {
      receivePrioritizedDeliveryMethod?: boolean;
      signal?: AbortSignal;
    },
  ) {
    const key = `${pos}-${deliveryMethod}`;
    const { receivePrioritizedDeliveryMethod = false, signal } = opts || {};

    let data: GetETAV3Response;

    try {
      const response = await ETARequests.getETAV3({
        pos,
        signal,
      });
      data = response.data;
    } catch (error) {
      this._clearPromise(key);
      throw error;
    }

    if (
      !data.warehouse ||
      !Object.keys(data.warehouse.deliveryMethods ?? {}).length
    ) {
      this.reset();
      this._clearPromise(key);

      return Promise.reject({
        message: 'No warehouse found',
        data,
      });
    }

    this._handleV3Response(data, receivePrioritizedDeliveryMethod);

    this._clearPromise(key);

    return this._transformV3ToLegacyResponse(data, deliveryMethod);
  }
  private _handleV3Response(
    data: GetETAV3Response,
    receivePrioritizedDeliveryMethod: boolean,
  ) {
    runInAction(() => {
      this.deliveryProviderByMethod = {};

      const methods = data.warehouse?.deliveryMethods ?? {};

      for (const [methodCode, methodVal] of Object.entries(methods)) {
        const provider = methodVal.provider;
        if (provider) {
          this.deliveryProviderByMethod[methodCode] = provider.toUpperCase();
        }
      }
    });

    if (!receivePrioritizedDeliveryMethod) {
      const existingMethods = Object.keys(data.warehouse.deliveryMethods).map(
        (name) =>
          `${NEW_TO_LEGACY_DELIVERY_METHOD_NAME[name]}` as ETADeliveryMethodType,
      );

      if (!existingMethods.includes(checkoutStore.deliveryMethod)) {
        const existing = existingMethods[0];

        if (existing) {
          checkoutStore.setDeliveryMethod(existing);
        }
      }

      return;
    }

    const prioritizedDeliveryMethod = getPrioritizedDeliveryMethod(
      data.warehouse.deliveryMethods,
    );

    if (prioritizedDeliveryMethod) {
      const legacyDeliveryMethod =
        `${NEW_TO_LEGACY_DELIVERY_METHOD_NAME[prioritizedDeliveryMethod]}` as ETADeliveryMethodType;

      checkoutStore.setDeliveryMethod(legacyDeliveryMethod);
    }
  }

  private _transformV3ToLegacyResponse(
    data: GetETAV3Response,
    requestedDeliveryMethod?: ETADeliveryMethodType,
  ): ETACalculation {
    const { warehouse } = data;

    const targetDeliveryMethod =
      requestedDeliveryMethod || checkoutStore.deliveryMethod;

    const paymentMethods: Partial<
      Record<ETADeliveryMethodType, ETAPaymentMethod[]>
    > = {};
    const cost: Record<string, ETACost> = {};
    const deliveryMethods: ETADeliveryMethod[] = [];
    let deliverySlots: DeliverySlotsResponse = {};
    const duration: { min: number; max: number; range: string } = {
      min: 0,
      max: 0,
      range: '0 - 0',
    };
    const zone: Record<string, { id: string; name: string }> = {};

    Object.values(warehouse.deliveryMethods).forEach((method) => {
      const {
        [DeliveryMechanic.PICKUP]: pickupMechanic,
        [DeliveryMechanic.ON_DEMAND]: onDemandMechanic,
        [DeliveryMechanic.SCHEDULED]: scheduledMechanic,
      } = method.deliveryMechanics;

      const existingCost = onDemandMechanic?.cost ?? pickupMechanic?.cost;
      const existingDuration =
        onDemandMechanic?.duration ?? pickupMechanic?.duration;

      const legacyMethod: ETADeliveryMethod = {
        cost: existingCost
          ? {
              ...existingCost,
              shippingPounds: mainStore.convertPenceToPounds(
                existingCost.shipping,
              ),
              thresholdPounds: mainStore.convertPenceToPounds(
                existingCost.threshold,
              ),
              serviceFeeThresholdPounds: mainStore.convertPenceToPounds(
                existingCost.serviceFeeThreshold,
              ),
              serviceFeeShippingPounds: mainStore.convertPenceToPounds(
                existingCost.serviceFee,
              ),
            }
          : DeliveryStore.DEFAULT_COST,
        deliveryMechanics: {
          JIFFY_DELIVERY: this._transformV3Mechanics(method.deliveryMechanics),
        },
        paymentMethods: method.zone.paymentMethods as ETAPaymentMethod[],
        sellers: ['JIFFY', 'jiffy'],
        type: NEW_TO_LEGACY_DELIVERY_METHOD_NAME[
          method.code.toUpperCase()
        ] as ETADeliveryMethodType,
      };

      deliveryMethods.push(legacyMethod);
      cost[legacyMethod.type] = legacyMethod.cost;
      paymentMethods[legacyMethod.type] = legacyMethod.paymentMethods;
      zone[legacyMethod.type] = method.zone;

      if (legacyMethod.type === targetDeliveryMethod && existingDuration) {
        duration.min = existingDuration.min;
        duration.max = existingDuration.max;
        duration.range = `${existingDuration.min} - ${existingDuration.max}`;
      }

      if (scheduledMechanic?.deliverySlots) {
        deliverySlots = scheduledMechanic.deliverySlots;
      }
    });

    return {
      cost,
      paymentMethods,
      deliverySlots,
      duration,
      warehouse: {
        id: warehouse.id,
        code: warehouse.code,
        address: warehouse.address,
        location: warehouse.location,
        zone,
        availability: {
          ...warehouse.availability,
          deliveryMechanics: this._transformV3Availability({
            deliveryMethods: warehouse.deliveryMethods,
            requestedDeliveryMethod: targetDeliveryMethod,
          }),
        },
        deliveryMethods,
      },
      // The data below not used in V3 and should be removed in whole system in future releases
      meta: {
        todayAsIndex: 0,
        serverTime: '',
      },
      distance: 0,
      highDemand: false,
    };
  }

  private _transformV3Mechanics(deliveryMechanics: MethodMechanics) {
    const {
      [DeliveryMechanic.ON_DEMAND]: onDemand,
      [DeliveryMechanic.SCHEDULED]: slot,
      [DeliveryMechanic.PICKUP]: pickup,
    } = deliveryMechanics;

    const generateLegacyMechanic = (mechanic: MechanicInstance) => ({
      order_amount_threshold: mechanic.orderAmountThreshold,
      schedule: mechanic.schedule.value,
    });

    if (pickup) {
      return {
        [MechanicType.ON_DEMAND]: generateLegacyMechanic(pickup),
      } as ETADeliveryMethodMechanics;
    }

    const mechanics: Record<string, unknown> = {};

    if (onDemand) {
      mechanics[MechanicType.ON_DEMAND] = generateLegacyMechanic(onDemand);
    }

    if (slot) {
      mechanics[MechanicType.SLOT] = generateLegacyMechanic(slot);
    }

    return mechanics as ETADeliveryMethodMechanics;
  }

  private _transformV3Availability({
    deliveryMethods,
    requestedDeliveryMethod,
  }: {
    deliveryMethods: WarehouseDeliveryMethods;
    requestedDeliveryMethod: ETADeliveryMethodType;
  }): ETAWarehouseMechanicsAvailability {
    return Object.values(
      deliveryMethods,
    ).reduce<ETAWarehouseMechanicsAvailability>((memo, method) => {
      const {
        [DeliveryMechanic.ON_DEMAND]: onDemandMechanic,
        [DeliveryMechanic.SCHEDULED]: scheduledMechanic,
        [DeliveryMechanic.PICKUP]: pickUpMechanic,
      } = method.deliveryMechanics;

      if (
        requestedDeliveryMethod === ETADeliveryMethodType.JiffyDelivery &&
        onDemandMechanic?.availability
      ) {
        memo[MechanicType.ON_DEMAND] = onDemandMechanic?.availability;
      }

      if (
        requestedDeliveryMethod === ETADeliveryMethodType.ClickAndCollect &&
        pickUpMechanic?.availability
      ) {
        memo[MechanicType.ON_DEMAND] = pickUpMechanic?.availability;
      }

      if (scheduledMechanic) {
        const slots = Object.values(
          scheduledMechanic.deliverySlots ?? {},
        ).flat();

        memo[MechanicType.SLOT] = {
          availableNow: !!slots.length,
          opening: '',
          closing: '',
          nextAvailabilityDay: 7,
          localTime: '',
        };
      }

      return memo;
    }, {});
  }

  private _clearPromise(key?: string) {
    if (!key) {
      this._requestPromiseHolder.clear();
      return;
    }

    if (this._requestPromiseHolder.has(key)) {
      this._requestPromiseHolder.delete(key);
    }
  }

  destroy(): void {
    // Clean up any resources or listeners here if necessary in future
    // Currently, no resources need to be explicitly released
  }
}

export default DeliveryStore;
